import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { useSiteLogo } from '../../context/SiteLogoContext';
import { BRAND } from '../../config/brand';
import './Auth.css';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();
  const { resolvedLogoUrl, logoAlt } = useSiteLogo();

  // Retry handler
  const handleRetry = () => {
    setError('');
    setLoading(false);
    // Form will be re-submitted when user clicks submit again
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    const result = await forgotPassword(email);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.message || 'Failed to send reset email');
    }

    setLoading(false);
  };

  if (success) {
    return (
      <>
        <Helmet>
          <title>Check Your Email — {BRAND.name} | Password Reset</title>
          <meta name="description" content={`Check your email for the password reset link from ${BRAND.name}.`} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="auth-page">
          <div className="auth-container">
            <div className="auth-header">
              <Link to="/" className="auth-logo">
                <img src={resolvedLogoUrl} alt={logoAlt || 'EVORA'} className="auth-logo-image" />
              </Link>
              <h1 className="auth-title">Check Your Email</h1>
            <p className="auth-subtitle">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
          </div>

          <div className="auth-success">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <p>Please check your inbox and click the link to reset your password.</p>
            <p className="auth-hint">The link will expire in 10 minutes.</p>
          </div>

          <Link to="/login" className="auth-button auth-button--primary">
            Back to Sign In
          </Link>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Forgot Password — {BRAND.name} | Reset Your Password</title>
        <meta name="description" content={`Reset your ${BRAND.name} account password. Enter your email to receive a password reset link.`} />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={`Forgot Password — ${BRAND.name}`} />
        <meta property="og:description" content="Reset your password to access your account." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src={resolvedLogoUrl} alt={logoAlt || 'EVORA'} className="auth-logo-image" />
          </Link>
          <h1 className="auth-title">Forgot Password</h1>
          <p className="auth-subtitle">
            Enter your email to receive a reset link. Signed in with Google? Use the same email — we&apos;ll send a link to set a password so you can sign in with email too.
          </p>
        </div>

        {error && (
          <div className="auth-error" role="alert" aria-live="assertive">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            {(error.includes('timeout') || error.includes('Network error')) && (
              <button
                type="button"
                className="auth-error-retry"
                onClick={handleRetry}
                aria-label="Retry password reset request"
              >
                Retry
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-group">
            <label htmlFor="email" className="auth-label">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              placeholder="your@email.com"
              required
              autoComplete="email"
            />
          </div>

          <button
            type="submit"
            className="auth-button auth-button--primary"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <p className="auth-footer">
          Remember your password?{' '}
          <Link to="/login" className="auth-link auth-link--primary">
            Sign in
          </Link>
        </p>
      </div>
    </div>
    </>
  );
}

export default ForgotPassword;
